/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleHelp, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export default function AshleyAdvantageAccountTooltip() {
    const { t } = useTranslation('checkout');
    const [open, setOpen] = useState(false);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    onMouseEnter={() => setOpen(true)}
                    onMouseLeave={() => setOpen(false)}
                    onFocus={() => setOpen(true)}
                    onBlur={() => setOpen(false)}
                    aria-label={t('ashleyAdvantage.accountNumberTooltipTriggerAriaLabel')}
                    className="inline-flex size-4 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
                    data-testid="aa-account-number-tooltip-trigger">
                    <CircleHelp className="size-4" aria-hidden />
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                side="bottom"
                sideOffset={8}
                onMouseEnter={() => setOpen(true)}
                onMouseLeave={() => setOpen(false)}
                className="w-[320px] rounded-xs border border-input bg-card p-4 text-foreground shadow-md"
                data-testid="aa-account-number-tooltip-content">
                <div className="space-y-4 text-sm leading-5">
                    <div className="flex items-start justify-between gap-3">
                        <p className="font-semibold">{t('ashleyAdvantage.accountNumberTooltip.synchronyCardTitle')}</p>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="text-muted-foreground hover:text-foreground"
                            aria-label={t('ashleyAdvantage.accountNumberTooltip.closeAriaLabel')}>
                            <X className="size-4" aria-hidden />
                        </button>
                    </div>
                    <div className="space-y-0.5 text-muted-foreground">
                        <p>{t('ashleyAdvantage.accountNumberTooltip.synchronyCardStartsWith')}</p>
                        <p>{t('ashleyAdvantage.accountNumberTooltip.synchronyCardDigits')}</p>
                        <p>{t('ashleyAdvantage.accountNumberTooltip.synchronyCardCvvRequired')}</p>
                    </div>
                    <div className="space-y-0.5">
                        <p className="font-semibold text-foreground">
                            {t('ashleyAdvantage.accountNumberTooltip.applicationReferenceTitle')}
                        </p>
                        <p className="text-muted-foreground">
                            {t('ashleyAdvantage.accountNumberTooltip.applicationReferenceStartsWith')}
                        </p>
                        <p className="text-muted-foreground">
                            {t('ashleyAdvantage.accountNumberTooltip.applicationReferenceDigits')}
                        </p>
                    </div>
                    <div className="space-y-0.5">
                        <p className="font-semibold text-foreground">
                            {t('ashleyAdvantage.accountNumberTooltip.checkoutCodeTitle')}
                        </p>
                        <p className="text-muted-foreground">
                            {t('ashleyAdvantage.accountNumberTooltip.checkoutCodeType')}
                        </p>
                        <p className="text-muted-foreground">
                            {t('ashleyAdvantage.accountNumberTooltip.checkoutCodeLength')}
                        </p>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}
